/**
 * Presentation helpers for the pawned items (`pawned_items`) attached to a contract.
 *
 * The Contracts page shows the items of the contract currently selected in the
 * contracts grid, so the raw entity rows have to be reduced to a short,
 * human readable name / details / quantity triple.
 */

export interface PawnedItem {
    id?: number | null;
    itemName?: string | null;
    description?: string | null;
    brand?: string | null;
    model?: string | null;
    category?: string | null;
    subcategory?: string | null;
    itemType?: string | null;
    cantitate?: number | string | null;
    unitOfMeasure?: string | null;
    weightGrams?: number | string | null;
    purityKarat?: string | null;
}

export interface PawnedItemRow {
    key: string;
    name: string;
    details: string;
    quantity: string;
}

const clean = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const toNumber = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/** Rounds to 2 decimals and drops trailing zeros (`2.50` -> `2.5`, `2.00` -> `2`). */
const formatAmount = (value: number): string => String(Number(value.toFixed(2)));

/**
 * Best available label for an item: the explicit name, otherwise brand/model,
 * otherwise the free text description or the category.
 */
export const formatPawnedItemName = (item: PawnedItem): string => {
    const brandModel = [clean(item.brand), clean(item.model)].filter(Boolean).join(' ');
    return (
        clean(item.itemName) ||
        brandModel ||
        clean(item.description) ||
        clean(item.category) ||
        clean(item.itemType)
    );
};

/**
 * Secondary line for an item (category / purity / brand-model when it is not
 * already used as the name). Empty when nothing extra is worth showing.
 */
export const formatPawnedItemDetails = (item: PawnedItem): string => {
    const name = formatPawnedItemName(item);
    const brandModel = [clean(item.brand), clean(item.model)].filter(Boolean).join(' ');
    const category = [clean(item.category), clean(item.subcategory)].filter(Boolean).join(' / ');
    const purity = clean(item.purityKarat);

    return [brandModel, category, purity].filter(part => part && part !== name).join(' • ');
};

/**
 * Quantity of an item: metal items are measured in grams, everything else by
 * unit count. Both are rounded to 2 decimals.
 */
export const formatPawnedItemQuantity = (item: PawnedItem): string => {
    const weight = toNumber(item.weightGrams);
    if (weight !== null && weight > 0) {
        return `${formatAmount(weight)} g`;
    }

    const count = toNumber(item.cantitate);
    if (count !== null && count > 0) {
        const unit = clean(item.unitOfMeasure) || 'buc';
        return `${formatAmount(count)} ${unit}`;
    }

    return '';
};

/**
 * Builds the display rows for a contract. When the contract has no detailed
 * item rows yet, the contract's own `obiect_amanetat` summary is used so the
 * panel is never empty for legacy contracts.
 */
export const buildPawnedItemRows = (
    items: PawnedItem[] | null | undefined,
    fallbackDescription?: string | null,
): PawnedItemRow[] => {
    const rows = (items ?? [])
        .map((item, index) => ({
            key: String(item.id ?? `row-${index}`),
            name: formatPawnedItemName(item),
            details: formatPawnedItemDetails(item),
            quantity: formatPawnedItemQuantity(item),
        }))
        .filter(row => row.name || row.details || row.quantity);

    if (rows.length > 0) {
        return rows;
    }

    const fallback = clean(fallbackDescription);
    return fallback ? [{ key: 'fallback', name: fallback, details: '', quantity: '' }] : [];
};
